// Windows EOL - End-of-Life Information
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import '../../App.css';
import './WindowsEOL.css';
import Footer from '../Footer';

export default function WindowsEOL() {
  const history = useHistory();
  const [eolData, setEolData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWindowsEOL();
  }, []);

  const fetchWindowsEOL = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('https://endoflife.date/api/windows.json');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setEolData(data);
    } catch (err) {
      console.error('Error fetching Windows EOL data:', err);
      setError('Failed to load Windows EOL data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isEOL = (eolDate) => {
    if (!eolDate) return false;
    return new Date(eolDate) < new Date();
  };

  const getStatusBadge = (eolDate, supportDate) => {
    const now = new Date();
    const eol = eolDate ? new Date(eolDate) : null;
    const support = supportDate ? new Date(supportDate) : null;

    if (eol && eol < now) {
      return <span className="status-badge status-eol">End of Life</span>;
    } else if (support && support < now) {
      return <span className="status-badge status-extended">Extended Support</span>;
    } else {
      return <span className="status-badge status-active">Active Support</span>;
    }
  };

  if (loading) {
    return (
      <div className="windows-eol-container">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading Windows EOL data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="windows-eol-container">
        <div className="error-state">
          <i className="fas fa-exclamation-circle"></i>
          <h2>Error Loading Data</h2>
          <p>{error}</p>
          <button onClick={fetchWindowsEOL} className="btn-retry">
            <i className="fas fa-sync"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="windows-eol-container">
        <div className="windows-eol-header">
          <button onClick={() => history.push('/tools')} className="btn-back-inline">
            <i className="fas fa-arrow-left"></i> Back to Tools
          </button>
          <h1>
            <i className="fab fa-windows"></i> Windows End-of-Life
          </h1>
          <p className="subtitle">Support and lifecycle information for Windows versions</p>
        </div>

        <div className="eol-info-banner">
          <i className="fas fa-info-circle"></i>
          <p>Data provided by <a href="https://endoflife.date" target="_blank" rel="noopener noreferrer">endoflife.date</a></p>
        </div>

        <div className="eol-grid">
          {eolData.map((version, index) => (
            <div key={index} className={`eol-card ${isEOL(version.eol) ? 'eol-expired' : ''}`}>
              <div className="eol-card-header">
                <h3>
                  {version.cycle}
                  {version.codename && <span className="codename"> ({version.codename})</span>}
                </h3>
                {getStatusBadge(version.eol, version.support)}
              </div>

              <div className="eol-card-body">
                <div className="eol-info-row">
                  <span className="eol-label">
                    <i className="fas fa-calendar-plus"></i> Release Date
                  </span>
                  <span className="eol-value">{formatDate(version.releaseDate)}</span>
                </div>

                {version.support && (
                  <div className="eol-info-row">
                    <span className="eol-label">
                      <i className="fas fa-life-ring"></i> Mainstream Support
                    </span>
                    <span className="eol-value">{formatDate(version.support)}</span>
                  </div>
                )}

                <div className="eol-info-row">
                  <span className="eol-label">
                    <i className="fas fa-calendar-times"></i> End of Life
                  </span>
                  <span className={`eol-value ${isEOL(version.eol) ? 'eol-date-expired' : 'eol-date-active'}`}>
                    {formatDate(version.eol)}
                  </span>
                </div>

                {version.latest && (
                  <div className="eol-info-row">
                    <span className="eol-label">
                      <i className="fas fa-tag"></i> Latest Version
                    </span>
                    <span className="eol-value">{version.latest}</span>
                  </div>
                )}

                {version.link && (
                  <div className="eol-info-row">
                    <a 
                      href={version.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="eol-link"
                    >
                      <i className="fas fa-external-link-alt"></i> More Information
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </>
  );
}
